/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * UatWidget - Floating UAT Testing Board Widget
 * ============================================================================
 *
 * A floating bottom-right widget that provides quick access to UAT test items.
 * Collapsed: pill-shaped button with progress ring.
 * Expanded: glass panel with category filters, item list, and inline feedback.
 *
 * Design: luxury car dashboard at night -- dark glassmorphism with soft
 * cyan/green/magenta accents. Calm 300-400ms animations. Color-blind safe
 * (icons + labels, not just color).
 *
 * ============================================================================
 */

import { useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MessageSquare,
  Check,
  X,
  Upload,
  Send,
} from 'lucide-react';
import { UatProgressRing } from './UatProgressRing';
import { UAT_TEST_ITEMS, UAT_CATEGORIES } from './uat-items';
import {
  useUatItems,
  useUatProgress,
  useUpdateUatStatus,
  useAddUatFeedback,
  useUatFeedback,
} from '@/hooks/api/useUat';
import type { UatItemDto, UatItemStatus } from '@/types/uat.types';
import GuideBrowser from '@/components/guides/GuideBrowser';
import ProcessGuideView from '@/components/guides/ProcessGuideView';
import type { ProcessGuide } from '@/types/process-guides.types';

/* -------------------------------------------------------------------------- */
/*  Status color map (color-blind safe: always paired with text labels)       */
/* -------------------------------------------------------------------------- */

const STATUS_COLORS: Record<UatItemStatus, string> = {
  TO_TEST: 'rgba(148, 163, 184, 0.4)',
  IN_PROGRESS: '#ffaa00',
  PASSED: '#00ff88',
  FAILED: '#ff00aa',
  CONFIRMED: '#00f0ff',
  DISPUTED: '#ff6b35',
  CLOSED: 'rgba(148, 163, 184, 0.2)',
};

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

export function UatWidget() {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();

  /* ---- data hooks ---- */
  const { data: progress } = useUatProgress();
  const { data: items } = useUatItems();
  const updateStatus = useUpdateUatStatus();
  const addFeedback = useAddUatFeedback();

  /* ---- local state ---- */
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'tests' | 'guides'>('tests');
  const [selectedGuide, setSelectedGuide] = useState<ProcessGuide | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedItemCode, setSelectedItemCode] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackFiles, setFeedbackFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ---- derived ---- */
  const done = progress
    ? progress.passed + progress.confirmed + progress.closed
    : 0;

  /* Build a lookup map: itemCode -> UatItemDto */
  const itemMap = useMemo(() => {
    const map = new Map<string, UatItemDto>();
    items?.forEach((it) => map.set(it.itemCode, it));
    return map;
  }, [items]);

  /* Filter test item definitions by category */
  const filteredDefs = useMemo(() => {
    if (!selectedCategory) return UAT_TEST_ITEMS;
    return UAT_TEST_ITEMS.filter((d) => d.category === selectedCategory);
  }, [selectedCategory]);

  /* Group filtered defs by category for rendering */
  const groupedDefs = useMemo(() => {
    const groups: Record<string, typeof UAT_TEST_ITEMS> = {};
    filteredDefs.forEach((d) => {
      (groups[d.category] ??= []).push(d);
    });
    return groups;
  }, [filteredDefs]);

  /* ---- handlers (must be above early returns to preserve hook order) ---- */

  const handleStatusChange = useCallback((code: string, status: string) => {
    updateStatus.mutate({ code, status });
  }, [updateStatus]);

  const handleSubmitFeedback = useCallback(() => {
    if (!selectedItemCode || !feedbackText.trim()) return;
    addFeedback.mutate(
      { code: selectedItemCode, message: feedbackText.trim(), files: feedbackFiles.length ? feedbackFiles : undefined },
      {
        onSuccess: () => {
          setFeedbackText('');
          setFeedbackFiles([]);
        },
      },
    );
  }, [selectedItemCode, feedbackText, feedbackFiles, addFeedback]);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).slice(0, 5);
    setFeedbackFiles((prev) => [...prev, ...dropped].slice(0, 5));
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selected = Array.from(e.target.files).slice(0, 5);
    setFeedbackFiles((prev) => [...prev, ...selected].slice(0, 5));
    e.target.value = '';
  }, []);

  const removeFile = useCallback((index: number) => {
    setFeedbackFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /* ---- early exit (after ALL hooks) ---- */
  if (!progress) return null;
  if (progress.percentComplete === 100) return null;

  /* ------------------------------------------------------------------ */
  /*  Collapsed pill                                                     */
  /* ------------------------------------------------------------------ */

  if (!isExpanded) {
    return (
      <div className="uat-widget-collapsed" onClick={() => setIsExpanded(true)}>
        <UatProgressRing
          percent={progress.percentComplete}
          size={36}
          strokeWidth={2.5}
        />
        <span className="uat-widget-collapsed__label">
          UAT {done}/{progress.total}
        </span>
        <style>{widgetStyles}</style>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Expanded panel                                                     */
  /* ------------------------------------------------------------------ */

  return (
    <div className="uat-widget" role="dialog" aria-label={t('uat.widget.title')}>
      {/* -- Header -- */}
      <div className="uat-widget__header">
        <div className="uat-widget__header-top">
          <span className="uat-widget__title">{t('uat.widget.title')}</span>
          <button
            className="uat-widget__minimize"
            onClick={() => setIsExpanded(false)}
            aria-label={t('uat.widget.minimize')}
          >
            <ChevronDown size={18} />
          </button>
        </div>
        <div className="uat-widget__progress-track">
          <div
            className="uat-widget__progress-fill"
            style={{ width: `${progress.percentComplete}%` }}
          />
        </div>
        <span className="uat-widget__subtitle">
          {done}/{progress.total} {t('uat.widget.completed')}
        </span>
      </div>

      {/* -- Tab bar -- */}
      <div className="uat-widget__tabs">
        <button
          className={`uat-widget__tab ${activeTab === 'tests' ? 'uat-widget__tab--active' : ''}`}
          onClick={() => { setActiveTab('tests'); setSelectedGuide(null); }}
        >
          Tests
        </button>
        <button
          className={`uat-widget__tab ${activeTab === 'guides' ? 'uat-widget__tab--active' : ''}`}
          onClick={() => setActiveTab('guides')}
        >
          Guides
        </button>
      </div>

      {/* -- Category filter + Item list (Tests tab) -- */}
      {activeTab === 'tests' && (<>
      <div className="uat-widget__filters-wrap">
        <button
          className="uat-widget__filters-arrow uat-widget__filters-arrow--left"
          onClick={() => {
            const el = document.querySelector('.uat-widget__filters');
            if (el) el.scrollBy({ left: -120, behavior: 'smooth' });
          }}
          aria-label="Scroll left"
        >
          ‹
        </button>
        <div className="uat-widget__filters">
          <button
            className={`uat-widget__filter-pill ${selectedCategory === null ? 'uat-widget__filter-pill--active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            {t('uat.categories.all')}
          </button>
          {UAT_CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`uat-widget__filter-pill ${selectedCategory === cat ? 'uat-widget__filter-pill--active' : ''}`}
              onClick={() => setSelectedCategory(cat === selectedCategory ? null : cat)}
            >
              {t(`uat.categories.${cat}`)}
            </button>
          ))}
        </div>
        <button
          className="uat-widget__filters-arrow uat-widget__filters-arrow--right"
          onClick={() => {
            const el = document.querySelector('.uat-widget__filters');
            if (el) el.scrollBy({ left: 120, behavior: 'smooth' });
          }}
          aria-label="Scroll right"
        >
          ›
        </button>
      </div>

      {/* -- Item list -- */}
      <div className="uat-widget__list">
        {Object.entries(groupedDefs).map(([category, defs]) => (
          <div key={category} className="uat-widget__group">
            <div className="uat-widget__group-header">
              {t(`uat.categories.${category}`)}
            </div>
            {defs.map((def) => {
              const item = itemMap.get(def.code);
              const status: UatItemStatus = item?.status ?? 'TO_TEST';
              const isSelected = selectedItemCode === def.code;

              return (
                <div key={def.code} className="uat-widget__item-wrapper">
                  <div
                    className={`uat-widget__item ${isSelected ? 'uat-widget__item--selected' : ''}`}
                    onClick={() =>
                      setSelectedItemCode(isSelected ? null : def.code)
                    }
                  >
                    {/* Status indicator bar */}
                    <div
                      className="uat-widget__item-bar"
                      style={{ backgroundColor: STATUS_COLORS[status] }}
                    />

                    {/* Title + badge */}
                    <div className="uat-widget__item-info">
                      <span className="uat-widget__item-title">
                        {t(`uat.items.${def.code}.title`)}
                      </span>
                      <span
                        className="uat-widget__item-badge"
                        style={{ color: STATUS_COLORS[status] }}
                      >
                        {t(`uat.status.${status}`)}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="uat-widget__item-actions">
                      <ItemActions
                        status={status}
                        code={def.code}
                        targetPath={def.targetPath}
                        feedbackCount={item?.feedbackCount ?? 0}
                        onStatusChange={handleStatusChange}
                        navigate={navigate}
                        t={t}
                      />
                    </div>
                  </div>

                  {/* Inline feedback */}
                  {isSelected && (
                    <InlineFeedback
                      code={def.code}
                      feedbackText={feedbackText}
                      feedbackFiles={feedbackFiles}
                      isSubmitting={addFeedback.isPending}
                      fileInputRef={fileInputRef}
                      onTextChange={setFeedbackText}
                      onFileDrop={handleFileDrop}
                      onFileSelect={handleFileSelect}
                      onRemoveFile={removeFile}
                      onSubmit={handleSubmitFeedback}
                      t={t}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      </>)}

      {/* -- Guides content -- */}
      {activeTab === 'guides' && !selectedGuide && (
        <div className="uat-widget__guides">
          <GuideBrowser onSelectGuide={setSelectedGuide} compact />
        </div>
      )}
      {activeTab === 'guides' && selectedGuide && (
        <div className="uat-widget__guides">
          <ProcessGuideView guide={selectedGuide} onBack={() => setSelectedGuide(null)} />
        </div>
      )}

      <style>{widgetStyles}</style>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Item action buttons                                                       */
/* -------------------------------------------------------------------------- */

function ItemActions({
  status,
  code,
  targetPath,
  feedbackCount,
  onStatusChange,
  navigate,
  t,
}: {
  status: UatItemStatus;
  code: string;
  targetPath: string;
  feedbackCount: number;
  onStatusChange: (code: string, status: string) => void;
  navigate: ReturnType<typeof import('react-router-dom').useNavigate>;
  t: (key: string) => string;
}) {
  const stopProp = (e: React.MouseEvent) => e.stopPropagation();

  switch (status) {
    case 'TO_TEST':
      return (
        <button
          className="uat-widget__btn uat-widget__btn--start"
          onClick={(e) => {
            stopProp(e);
            onStatusChange(code, 'IN_PROGRESS');
          }}
        >
          {t('uat.actions.start')}
        </button>
      );

    case 'IN_PROGRESS':
      return (
        <>
          <button
            className="uat-widget__btn uat-widget__btn--go"
            onClick={(e) => {
              stopProp(e);
              navigate(targetPath);
            }}
            title={t('uat.actions.goToPage')}
          >
            <ExternalLink size={14} />
          </button>
          <button
            className="uat-widget__btn uat-widget__btn--pass"
            onClick={(e) => {
              stopProp(e);
              onStatusChange(code, 'PASSED');
            }}
          >
            <Check size={14} />
          </button>
          <button
            className="uat-widget__btn uat-widget__btn--fail"
            onClick={(e) => {
              stopProp(e);
              onStatusChange(code, 'FAILED');
            }}
          >
            <X size={14} />
          </button>
        </>
      );

    case 'PASSED':
    case 'FAILED':
      return (
        <>
          <button
            className="uat-widget__btn uat-widget__btn--go"
            onClick={(e) => {
              stopProp(e);
              navigate(targetPath);
            }}
            title={t('uat.actions.goToPage')}
          >
            <ExternalLink size={14} />
          </button>
          <button
            className="uat-widget__btn uat-widget__btn--feedback"
            onClick={stopProp}
          >
            <MessageSquare size={14} />
            {feedbackCount > 0 && (
              <span className="uat-widget__feedback-count">{feedbackCount}</span>
            )}
          </button>
        </>
      );

    case 'CONFIRMED':
    case 'DISPUTED':
    case 'CLOSED':
    default:
      return null;
  }
}

/* -------------------------------------------------------------------------- */
/*  Inline feedback form                                                      */
/* -------------------------------------------------------------------------- */

function InlineFeedback({
  code,
  feedbackText,
  feedbackFiles,
  isSubmitting,
  fileInputRef,
  onTextChange,
  onFileDrop,
  onFileSelect,
  onRemoveFile,
  onSubmit,
  t,
}: {
  code: string;
  feedbackText: string;
  feedbackFiles: File[];
  isSubmitting: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onTextChange: (v: string) => void;
  onFileDrop: (e: React.DragEvent) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (i: number) => void;
  onSubmit: () => void;
  t: (key: string) => string;
}) {
  const { data: existingFeedback } = useUatFeedback(code);

  return (
    <div className="uat-widget__feedback">
      {/* Existing feedback */}
      {existingFeedback && existingFeedback.length > 0 && (
        <div className="uat-widget__feedback-list">
          {existingFeedback.map((fb) => (
            <div key={fb.id} className="uat-widget__feedback-entry">
              <span className="uat-widget__feedback-author">{fb.authorName}</span>
              <span className="uat-widget__feedback-msg">{fb.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Textarea */}
      <textarea
        className="uat-widget__feedback-input"
        rows={2}
        placeholder={t('uat.feedback.placeholder')}
        value={feedbackText}
        onChange={(e) => onTextChange(e.target.value)}
      />

      {/* Drop zone */}
      <div
        className="uat-widget__dropzone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onFileDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload size={14} />
        <span>{t('uat.feedback.dropzone')}</span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          style={{ display: 'none' }}
          onChange={onFileSelect}
        />
      </div>

      {/* File previews */}
      {feedbackFiles.length > 0 && (
        <div className="uat-widget__file-previews">
          {feedbackFiles.map((f, i) => (
            <div key={i} className="uat-widget__file-thumb">
              <span className="uat-widget__file-name">{f.name}</span>
              <button
                className="uat-widget__file-remove"
                onClick={() => onRemoveFile(i)}
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Submit */}
      <button
        className="uat-widget__submit"
        onClick={onSubmit}
        disabled={isSubmitting || !feedbackText.trim()}
      >
        <Send size={14} />
        {t('uat.feedback.submit')}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Styles                                                                    */
/* -------------------------------------------------------------------------- */

const widgetStyles = `
  /* ------------------------------------------------------------------ */
  /* Collapsed pill                                                      */
  /* ------------------------------------------------------------------ */
  .uat-widget-collapsed {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 999;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px 6px 8px;
    background: rgba(15, 20, 35, 0.92);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(0, 240, 255, 0.15);
    border-radius: 999px;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
    animation: uat-collapsed-glow 3s ease-in-out infinite;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    user-select: none;
  }

  .uat-widget-collapsed:hover {
    transform: scale(1.04);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5), 0 0 12px rgba(0, 240, 255, 0.15);
  }

  .uat-widget-collapsed__label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    letter-spacing: 0.02em;
  }

  @keyframes uat-collapsed-glow {
    0%, 100% {
      border-color: rgba(0, 240, 255, 0.15);
    }
    50% {
      border-color: rgba(0, 240, 255, 0.3);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Expanded panel                                                      */
  /* ------------------------------------------------------------------ */
  .uat-widget {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 999;
    width: 380px;
    max-height: 520px;
    display: flex;
    flex-direction: column;
    background: rgba(15, 20, 35, 0.92);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(0, 240, 255, 0.12);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 240, 255, 0.06);
    animation: uat-widget-enter 0.3s cubic-bezier(0.22, 1, 0.36, 1) both;
    overflow: hidden;
  }

  @keyframes uat-widget-enter {
    from {
      opacity: 0;
      transform: translateY(12px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  /* ---- Header ---- */
  .uat-widget__header {
    padding: 14px 16px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  .uat-widget__header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .uat-widget__title {
    font-size: 0.9375rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.92);
    letter-spacing: 0.02em;
  }

  .uat-widget__minimize {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.6);
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
  }

  .uat-widget__minimize:hover {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
  }

  .uat-widget__progress-track {
    height: 4px;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 2px;
    overflow: hidden;
  }

  .uat-widget__progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #00f0ff, #00ff88);
    border-radius: 2px;
    transition: width 0.5s ease;
  }

  .uat-widget__subtitle {
    display: block;
    margin-top: 6px;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.45);
  }

  /* ---- Tab bar ---- */
  .uat-widget__tabs {
    display: flex;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }
  .uat-widget__tab {
    flex: 1;
    padding: 8px;
    font-size: 0.75rem;
    font-weight: 600;
    text-align: center;
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    transition: color 0.15s ease, border-color 0.15s ease;
    border-bottom: 2px solid transparent;
  }
  .uat-widget__tab:hover {
    color: rgba(255, 255, 255, 0.7);
  }
  .uat-widget__tab--active {
    color: #00f0ff;
    border-bottom-color: #00f0ff;
  }

  /* ---- Guides container ---- */
  .uat-widget__guides {
    flex: 1;
    overflow-y: auto;
    max-height: 340px;
  }

  /* ---- Category filters ---- */
  .uat-widget__filters-wrap {
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  }

  .uat-widget__filters-arrow {
    flex-shrink: 0;
    width: 22px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.3);
    font-size: 1rem;
    cursor: pointer;
    transition: color 0.15s ease, background 0.15s ease;
    border-radius: 4px;
  }

  .uat-widget__filters-arrow:hover {
    color: #00f0ff;
    background: rgba(0, 240, 255, 0.08);
  }

  .uat-widget__filters {
    display: flex;
    gap: 4px;
    padding: 8px 4px;
    overflow-x: auto;
    flex: 1;
    min-width: 0;
    scrollbar-width: none;
  }

  .uat-widget__filters::-webkit-scrollbar {
    display: none;
  }

  .uat-widget__filter-pill {
    flex-shrink: 0;
    padding: 3px 10px;
    font-size: 0.6875rem;
    font-weight: 500;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 999px;
    background: transparent;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .uat-widget__filter-pill:hover {
    border-color: rgba(0, 240, 255, 0.2);
    color: rgba(255, 255, 255, 0.8);
  }

  .uat-widget__filter-pill--active {
    background: rgba(0, 240, 255, 0.12);
    border-color: rgba(0, 240, 255, 0.3);
    color: #00f0ff;
    box-shadow: 0 0 8px rgba(0, 240, 255, 0.1);
  }

  /* ---- Item list ---- */
  .uat-widget__list {
    flex: 1;
    overflow-y: auto;
    max-height: 340px;
    padding: 4px 0;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.08) transparent;
  }

  .uat-widget__group-header {
    padding: 6px 16px 3px;
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.3);
  }

  .uat-widget__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 12px 7px 0;
    margin: 0 8px;
    cursor: pointer;
    border-radius: 8px;
    transition: background 0.15s ease;
  }

  .uat-widget__item:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  .uat-widget__item--selected {
    background: rgba(0, 240, 255, 0.05);
  }

  .uat-widget__item-bar {
    width: 4px;
    align-self: stretch;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .uat-widget__item-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .uat-widget__item-title {
    font-size: 0.8125rem;
    color: rgba(255, 255, 255, 0.82);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .uat-widget__item-badge {
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .uat-widget__item-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
  }

  /* ---- Action buttons ---- */
  .uat-widget__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 3px 8px;
    font-size: 0.6875rem;
    font-weight: 600;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: transform 0.12s ease, opacity 0.15s ease;
    white-space: nowrap;
  }

  .uat-widget__btn:active {
    transform: scale(0.95);
  }

  .uat-widget__btn--start {
    background: rgba(0, 240, 255, 0.15);
    color: #00f0ff;
  }

  .uat-widget__btn--start:hover {
    background: rgba(0, 240, 255, 0.25);
  }

  .uat-widget__btn--pass {
    background: rgba(0, 255, 136, 0.15);
    color: #00ff88;
    padding: 3px 6px;
  }

  .uat-widget__btn--pass:hover {
    background: rgba(0, 255, 136, 0.25);
  }

  .uat-widget__btn--fail {
    background: rgba(255, 0, 170, 0.15);
    color: #ff00aa;
    padding: 3px 6px;
  }

  .uat-widget__btn--fail:hover {
    background: rgba(255, 0, 170, 0.25);
  }

  .uat-widget__btn--go {
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.6);
    padding: 3px 6px;
  }

  .uat-widget__btn--go:hover {
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.1);
  }

  .uat-widget__btn--feedback {
    position: relative;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.6);
    padding: 3px 6px;
  }

  .uat-widget__btn--feedback:hover {
    color: rgba(255, 255, 255, 0.9);
  }

  .uat-widget__feedback-count {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 14px;
    height: 14px;
    font-size: 0.5625rem;
    font-weight: 700;
    line-height: 14px;
    text-align: center;
    background: #ff00aa;
    color: #fff;
    border-radius: 999px;
    padding: 0 3px;
  }

  /* ---- Inline feedback ---- */
  .uat-widget__feedback {
    padding: 8px 12px 10px 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.04);
    animation: uat-feedback-enter 0.25s ease both;
  }

  @keyframes uat-feedback-enter {
    from { opacity: 0; max-height: 0; }
    to { opacity: 1; max-height: 300px; }
  }

  .uat-widget__feedback-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 80px;
    overflow-y: auto;
    scrollbar-width: thin;
  }

  .uat-widget__feedback-entry {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
  }

  .uat-widget__feedback-author {
    font-weight: 600;
    color: rgba(0, 240, 255, 0.8);
    margin-right: 6px;
  }

  .uat-widget__feedback-msg {
    color: rgba(255, 255, 255, 0.55);
  }

  .uat-widget__feedback-input {
    width: 100%;
    padding: 6px 8px;
    font-size: 0.75rem;
    font-family: inherit;
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    resize: vertical;
    min-height: 36px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;
  }

  .uat-widget__feedback-input:focus {
    outline: none;
    border-color: rgba(0, 240, 255, 0.3);
    box-shadow: 0 0 0 2px rgba(0, 240, 255, 0.08);
  }

  .uat-widget__dropzone {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px;
    border: 1px dashed rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    font-size: 0.6875rem;
    color: rgba(255, 255, 255, 0.35);
    cursor: pointer;
    transition: border-color 0.2s ease, color 0.2s ease;
  }

  .uat-widget__dropzone:hover {
    border-color: rgba(0, 240, 255, 0.25);
    color: rgba(255, 255, 255, 0.55);
  }

  .uat-widget__file-previews {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  .uat-widget__file-thumb {
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 2px 6px;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 4px;
    font-size: 0.625rem;
    color: rgba(255, 255, 255, 0.6);
    max-width: 120px;
  }

  .uat-widget__file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .uat-widget__file-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;
  }

  .uat-widget__file-remove:hover {
    color: #ff00aa;
  }

  .uat-widget__submit {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 6px 12px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(135deg, #00f0ff, #00ff88);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: opacity 0.2s ease, transform 0.12s ease;
  }

  .uat-widget__submit:hover:not(:disabled) {
    opacity: 0.9;
  }

  .uat-widget__submit:active:not(:disabled) {
    transform: scale(0.97);
  }

  .uat-widget__submit:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* ---- Reduced motion ---- */
  @media (prefers-reduced-motion: reduce) {
    .uat-widget-collapsed {
      animation: none !important;
    }
    .uat-widget {
      animation: none !important;
    }
    .uat-widget__feedback {
      animation: none !important;
    }
    .uat-widget__btn {
      transition: none !important;
    }
    .uat-widget__progress-fill {
      transition: none !important;
    }
    .uat-widget-collapsed {
      transition: none !important;
    }
  }

  /* ---- Mobile ---- */
  @media (max-width: 480px) {
    .uat-widget {
      width: calc(100vw - 16px);
      right: 8px;
      bottom: 8px;
      max-height: 70vh;
    }
    .uat-widget-collapsed {
      right: 12px;
      bottom: 12px;
    }
  }
`;
